import React, { useEffect, useState, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Box,
  Button,
  Typography,
  Divider,
  Checkbox,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ClickAwayListener,
  Chip,
  IconButton,
  InputAdornment,
  Paper,
  Popper,
  SxProps,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import MembersIcon from "../../assets/common/space-members-icon.svg";
import ClientsIcon from "../../assets/common/space-clients-icon.svg";
import ActionItemService from "../../services/actionItems/ActionItemService";
import useDisplaySnackbar from "../../utils/useDisplaySnackbar";
import { useQueryClient } from "@tanstack/react-query";
import CustomConfirmation from "../Common/CustomConfirmation";
import { getAvatarColor, getAvatarInitials } from "../../utils/utils";
import useGlobalContext from "../../context/useGlobalContext";
import "../../styles/common.css";

interface CreateSpaceProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  activeUsers?: {
    name: string;
    email: string;
    pictureUrl?: string;
    profileOrgRelationshipId: string;
  }[];
  mode?: "create" | "edit";
  spaceData?: {
    id: string;
    title: string;
    spaceMemberProfileOrgIds: string[];
    subOrgIds?: string[];
  };
  activeClients?: any[];
}

export interface SpaceMultiSelectProps<T> {
  iconSrc: string;
  sectionLabel: string;
  required?: boolean;
  searchPlaceholder: string;
  searchValue: string;
  onSearchChange: (value: string) => void;
  expanded: boolean;
  onOpenDropdown: () => void;
  onCloseDropdown: () => void;
  selectedChips: { id: string; label: string }[];
  onRemoveChip: (id: string) => void;
  options: T[];
  tempSelectedIds: string[];
  onToggleTempItem: (id: string) => void;
  onApply: () => void;
  getOptionId: (option: T) => string;
  getOptionName: (option: T) => string;
  renderAvatar?: (option: T) => React.ReactNode;
  emptyText: string;
  containerSx?: SxProps;
}

export function SpaceMultiSelect<T>({
  iconSrc,
  sectionLabel,
  required,
  searchPlaceholder,
  searchValue,
  onSearchChange,
  expanded,
  onOpenDropdown,
  onCloseDropdown,
  selectedChips,
  onRemoveChip,
  options,
  tempSelectedIds,
  onToggleTempItem,
  onApply,
  getOptionId,
  getOptionName,
  renderAvatar,
  emptyText,
  containerSx,
}: SpaceMultiSelectProps<T>) {
  const inputRef = useRef<HTMLDivElement | null>(null);
  const popperRef = useRef<HTMLDivElement | null>(null);

  const handleClickAway = (event: MouseEvent | TouchEvent) => {
    if (
      popperRef.current &&
      popperRef.current.contains(event.target as Node)
    ) {
      return;
    }
    onCloseDropdown();
  };

  return (
    <Box sx={containerSx}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, mb: 1 }}>
        <img src={iconSrc} alt={sectionLabel} />
        <Typography className="create-space-section-label">
          {sectionLabel}{" "}
          {required && <span style={{ color: "#EF4444" }}>*</span>}
        </Typography>
      </Box>

      {/* Chips Display */}
      {selectedChips.length > 0 && (
        <Box className="selected-members-container">
          {selectedChips.map((chip) => (
            <Chip
              key={chip.id}
              label={chip.label}
              onDelete={() => onRemoveChip(chip.id)}
              deleteIcon={
                <CloseIcon
                  sx={{ fontSize: "14px !important", color: "#6B7280" }}
                />
              }
              className="create-space-member-chip"
            />
          ))}
        </Box>
      )}

      {/* Search Input & Floating Popper Dropdown */}
      <ClickAwayListener onClickAway={handleClickAway}>
        <Box ref={inputRef} sx={{ position: "relative", width: "100%" }}>
          <TextField
            variant="outlined"
            placeholder={searchPlaceholder}
            value={searchValue}
            onChange={(e) => onSearchChange(e.target.value)}
            onFocus={onOpenDropdown}
            fullWidth
            name="space-textfield"
            autoComplete="off"
            inputProps={{
              "aria-expanded": expanded,
              "aria-haspopup": "listbox",
            }}
            InputProps={{
              autoComplete: "chrome-off",
              spellCheck: false,
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "#9CA3AF", fontSize: 20 }} />
                </InputAdornment>
              ),
            }}
            className="create-space-input-root create-space-search-input"
          />

          <Popper
            open={expanded}
            anchorEl={inputRef.current}
            placement="bottom-start"
            style={{
              zIndex: 1400,
              width: inputRef.current
                ? inputRef.current.clientWidth
                : "100%",
            }}
            modifiers={[
              {
                name: "offset",
                options: {
                  offset: [0, 4],
                },
              },
            ]}
          >
            <Paper ref={popperRef} elevation={4} className="create-space-dropdown-paper">
              <div className="create-space-dropdown-list">
                <List dense disablePadding sx={{ py: 0.5 }}>
                  {options.length > 0 ? (
                    options.map((option, index) => {
                      const id = getOptionId(option);
                      const name = getOptionName(option);
                      const isChecked = tempSelectedIds.includes(id);
                      return (
                        <ListItem
                          key={id || index}
                          onClick={() => onToggleTempItem(id)}
                          sx={{
                            px: 2,
                            py: 0.75,
                            cursor: "pointer",
                            "&:hover": { backgroundColor: "#F8FAFC" },
                          }}
                          secondaryAction={
                            <Checkbox
                              checked={isChecked}
                              onChange={() => onToggleTempItem(id)}
                              onClick={(e) => e.stopPropagation()}
                              size="small"
                              sx={{
                                color: "#CBD5E1",
                                "&.Mui-checked": { color: "#7C3AED" },
                              }}
                            />
                          }
                        >
                          {renderAvatar && renderAvatar(option)}
                          <ListItemText
                            primary={name}
                            primaryTypographyProps={{
                              fontSize: 13,
                              fontWeight: 500,
                              color: "#1F2937",
                            }}
                          />
                        </ListItem>
                      );
                    })
                  ) : (
                    <Typography
                      sx={{
                        p: 2,
                        fontSize: 13,
                        color: "#6B7280",
                        textAlign: "center",
                      }}
                    >
                      {emptyText}
                    </Typography>
                  )}
                </List>
              </div>
              <Button
                variant="contained"
                fullWidth
                onClick={onApply}
                className="create-space-dropdown-add-btn"
              >
                Add
              </Button>
            </Paper>
          </Popper>
        </Box>
      </ClickAwayListener>
    </Box>
  );
}

const CreateSpace: React.FC<CreateSpaceProps> = ({
  open,
  onClose,
  onSuccess,
  activeUsers = [],
  mode,
  spaceData,
  activeClients = [],
}) => {
  const [name, setName] = useState("");
  const [selectedMembers, setSelectedMembers] = useState<string[]>([]);
  const [tempSelectedMembers, setTempSelectedMembers] = useState<string[]>([]);
  const [selectedClients, setSelectedClients] = useState<string[]>([]);
  const [tempSelectedClients, setTempSelectedClients] = useState<string[]>([]);
  const [searchValue, setSearchValue] = useState("");
  const [clientSearchValue, setClientSearchValue] = useState("");
  const [expanded, setExpanded] = useState(false);
  const [clientExpanded, setClientExpanded] = useState(false);
  const { showSnackbar } = useDisplaySnackbar();
  const queryClient = useQueryClient();
  const { accountDetails } = useGlobalContext();
  const loggedInAccountId = accountDetails?.accountId ?? "";
  const [nameError, setNameError] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const [initialMemberIds, setInitialMemberIds] = useState<string[]>([]);
  const [initialClientIds, setInitialClientIds] = useState<string[]>([]);

  const createSpaceMutation = ActionItemService.useCreateSpaces({
    onSuccess: () => {
      showSnackbar("success", "Space created successfully!");
      handleCloseAndReset();
      onSuccess?.();
      queryClient.invalidateQueries({ queryKey: ["get_spaces"] });
      queryClient.invalidateQueries({ queryKey: ["get_user_groups"] });
    },
    onError: () => {
      showSnackbar("error", "Failed to create space");
    },
  });

  const updateSpaceMutation = ActionItemService.useUpdateSpaces({
    onSuccess: () => {
      showSnackbar("success", "Space updated successfully!");
      handleCloseAndReset();
      onSuccess?.();
      queryClient.invalidateQueries({ queryKey: ["get_spaces"] });
      queryClient.invalidateQueries({ queryKey: ["get_user_groups"] });
    },
    onError: () => {
      showSnackbar("error", "Failed to update space");
    },
  });

  const deleteSpaceMutation = ActionItemService.useDeleteSpace({
    onSuccess: () => {
      showSnackbar("success", "Space deleted successfully!");
      onSuccess?.();
      setDeleteDialogOpen(false);
      onClose();
      queryClient.invalidateQueries({ queryKey: ["get_spaces"] });
      queryClient.invalidateQueries({ queryKey: ["get_user_groups"] });
    },
    onError: () => {
      showSnackbar("error", "Failed to delete space");
    },
  });

  const handleDelete = () => {
    if (spaceData?.id) {
      deleteSpaceMutation.mutate({
        accountId: loggedInAccountId,
        spaceId: spaceData.id,
      });
    }
  };

  const handleCloseAndReset = () => {
    setName("");
    setSelectedMembers([]);
    setTempSelectedMembers([]);
    setInitialMemberIds([]);
    setSelectedClients([]);
    setTempSelectedClients([]);
    setInitialClientIds([]);
    setSearchValue("");
    setClientSearchValue("");
    setExpanded(false);
    setClientExpanded(false);
    setNameError(false);
    setDeleteDialogOpen(false);
    onClose();
  };

  const handleOpenMembersDropdown = () => {
    setTempSelectedMembers(selectedMembers);
    setExpanded(true);
  };

  const handleToggleTempMember = (profileOrgRelationshipId: string) => {
    setTempSelectedMembers((prev) =>
      prev.includes(profileOrgRelationshipId)
        ? prev.filter((m) => m !== profileOrgRelationshipId)
        : [...prev, profileOrgRelationshipId]
    );
  };

  const handleApplyMembers = () => {
    setSelectedMembers(tempSelectedMembers);
    setExpanded(false);
  };

  const handleRemoveChip = (profileOrgRelationshipId: string) => {
    setSelectedMembers((prev) =>
      prev.filter((m) => m !== profileOrgRelationshipId)
    );
    setTempSelectedMembers((prev) =>
      prev.filter((m) => m !== profileOrgRelationshipId)
    );
  };

  const handleOpenClientsDropdown = () => {
    setTempSelectedClients(selectedClients);
    setClientExpanded(true);
  };

  const handleToggleTempClient = (clientId: string) => {
    setTempSelectedClients((prev) =>
      prev.includes(clientId)
        ? prev.filter((c) => c !== clientId)
        : [...prev, clientId]
    );
  };

  const handleApplyClients = () => {
    setSelectedClients(tempSelectedClients);
    setClientExpanded(false);
  };

  const handleRemoveClientChip = (clientId: string) => {
    setSelectedClients((prev) => prev.filter((c) => c !== clientId));
    setTempSelectedClients((prev) => prev.filter((c) => c !== clientId));
  };

  const getClientId = (c: any) => c.subOrgId || c.clientOrgId || c.id || "";
  const getClientName = (c: any) => c.subOrgName || c.clientName || c.name || "";

  const handleSave = () => {
    if (!name.trim()) {
      showSnackbar("warning", "Please enter a space name");
      setNameError(true);
      return;
    }
    setNameError(false);
    const payload: any = {
      title: name.trim(),
      spaceMemberProfileOrgIds: selectedMembers,
      subOrgIds: selectedClients,
    };

    let removeMemberProfileOrgIds: string[] = [];
    if (mode === "edit" && spaceData?.spaceMemberProfileOrgIds) {
      removeMemberProfileOrgIds = spaceData.spaceMemberProfileOrgIds.filter(
        (id) => !selectedMembers.includes(id)
      );
      payload.removeMemberProfileOrgIds = removeMemberProfileOrgIds;
    }

    let removeSubOrgIds: string[] = [];
    if (mode === "edit" && spaceData?.subOrgIds) {
      removeSubOrgIds = spaceData.subOrgIds.filter(
        (id) => !selectedClients.includes(id)
      );
      payload.removeSubOrgIds = removeSubOrgIds;
    }

    if (mode === "edit" && spaceData?.id) {
      updateSpaceMutation.mutate({
        ...payload,
        spaceId: spaceData.id,
      });
    } else {
      createSpaceMutation.mutate(payload);
    }
  };

  const filteredUsers = activeUsers.filter(
    (user) =>
      user.name.toLowerCase().includes(searchValue.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchValue.toLowerCase())
  );

  const selectedUsers = activeUsers.filter((u) =>
    selectedMembers.includes(u.profileOrgRelationshipId)
  );

  const allClientOptions = React.useMemo(() => {
    const map = new Map<string, any>();
    activeClients.forEach((c) => {
      const id = getClientId(c);
      if (id) map.set(id, c);
    });
    return Array.from(map.values());
  }, [activeClients]);

  const filteredClients = React.useMemo(() => {
    if (!clientSearchValue) return activeClients;
    return activeClients.filter((client) => {
      const clientName = getClientName(client);
      return (
        clientName.toLowerCase().includes(clientSearchValue.toLowerCase())
      );
    });
  }, [clientSearchValue, activeClients]);

  const selectedClientsList = React.useMemo(() => {
    return allClientOptions.filter((c) => {
      const id = getClientId(c);
      return selectedClients.includes(id);
    });
  }, [allClientOptions, selectedClients]);

  useEffect(() => {
    if (mode === "edit" && spaceData) {
      setName(spaceData.title || "");
      setSelectedMembers(spaceData.spaceMemberProfileOrgIds || []);
      setTempSelectedMembers(spaceData.spaceMemberProfileOrgIds || []);
      setInitialMemberIds(spaceData.spaceMemberProfileOrgIds || []);
      setSelectedClients(spaceData.subOrgIds || []);
      setTempSelectedClients(spaceData.subOrgIds || []);
      setInitialClientIds(spaceData.subOrgIds || []);
    }
  }, [mode, spaceData]);

  const areArraysEqual = (arr1: string[], arr2: string[]) => {
    if (arr1.length !== arr2.length) return false;
    const set2 = new Set(arr2);
    return arr1.every((item) => set2.has(item));
  };

  const isFormValid = React.useMemo(() => {
    if (!name.trim()) return false;

    if (mode === "edit") {
      const initialTitle = spaceData?.title || "";
      const nameChanged = name.trim() !== initialTitle.trim();
      const membersChanged = !areArraysEqual(selectedMembers, initialMemberIds);
      const clientsChanged = !areArraysEqual(selectedClients, initialClientIds);

      return nameChanged || membersChanged || clientsChanged;
    } else {
      return selectedMembers.length > 0;
    }
  }, [name, selectedMembers, selectedClients, initialMemberIds, initialClientIds, mode, spaceData]);

  return (
    <>
      <Dialog
        open={open}
        onClose={handleCloseAndReset}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          className: "create-space-paper",
        }}
      >
        <DialogTitle className="create-space-header">
          <IconButton
            onClick={handleCloseAndReset}
            className="create-space-close-btn"
          >
            <CloseIcon sx={{ fontSize: "18px" }} />
          </IconButton>

          <Typography className="create-space-title">
            {mode === "edit" ? "Edit Space" : "Create a Space"}
          </Typography>
          <Typography className="create-space-subtitle">
            A space represents teams, departments, or groups.
          </Typography>
        </DialogTitle>

        <DialogContent
          className="create-space-content">
          {/* ICON & NAME */}
          <Box>
            <Typography className="create-space-section-label" sx={{ mb: 1 }}>
              ICON & NAME <span style={{ color: "#EF4444" }}>*</span>
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box className="create-space-icon-box">
                {getAvatarInitials(name || "M")}
              </Box>
              <TextField
                variant="outlined"
                placeholder="e.g. Marketing, Engineering, HR..."
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  if (nameError && e.target.value.trim() !== "") {
                    setNameError(false);
                  }
                }}
                fullWidth
                error={nameError}
                name="space-textfield"
                id="space-textfield"
                autoComplete="off"
                inputProps={{
                  autoComplete: "chrome-off",
                  spellCheck: false,
                }}
                className="create-space-input-root"
              />
            </Box>
          </Box>

          {/* ADD MEMBERS */}
          <SpaceMultiSelect
            iconSrc={MembersIcon}
            sectionLabel="ADD MEMBERS"
            required
            searchPlaceholder="Search by name or enter email..."
            searchValue={searchValue}
            onSearchChange={(val) => {
              setSearchValue(val);
              if (!expanded) handleOpenMembersDropdown();
            }}
            expanded={expanded}
            onOpenDropdown={handleOpenMembersDropdown}
            onCloseDropdown={() => setExpanded(false)}
            selectedChips={selectedUsers.map((u) => ({
              id: u.profileOrgRelationshipId,
              label: u.name,
            }))}
            onRemoveChip={handleRemoveChip}
            options={filteredUsers}
            tempSelectedIds={tempSelectedMembers}
            onToggleTempItem={handleToggleTempMember}
            onApply={handleApplyMembers}
            getOptionId={(u) => u.profileOrgRelationshipId}
            getOptionName={(u) => u.name}
            renderAvatar={(u) => (
              <ListItemAvatar sx={{ minWidth: 36 }}>
                <Avatar
                  src={u.pictureUrl || undefined}
                  sx={{
                    width: 28,
                    height: 28,
                    fontSize: "12px",
                    fontWeight: 600,
                    bgcolor: !u.pictureUrl
                      ? getAvatarColor(u.email || u.name)
                      : undefined,
                  }}
                >
                  {!u.pictureUrl && getAvatarInitials(u.email || u.name)}
                </Avatar>
              </ListItemAvatar>
            )}
            emptyText="No members found"
          />

          {/* ADD CLIENTS */}
          <SpaceMultiSelect
            iconSrc={ClientsIcon}
            sectionLabel="ADD CLIENTS"
            searchPlaceholder="Search client by name.."
            searchValue={clientSearchValue}
            onSearchChange={(val) => {
              setClientSearchValue(val);
              if (!clientExpanded) handleOpenClientsDropdown();
            }}
            expanded={clientExpanded}
            onOpenDropdown={handleOpenClientsDropdown}
            onCloseDropdown={() => setClientExpanded(false)}
            selectedChips={selectedClientsList.map((c) => ({
              id: getClientId(c),
              label: getClientName(c),
            }))}
            onRemoveChip={handleRemoveClientChip}
            options={filteredClients}
            tempSelectedIds={tempSelectedClients}
            onToggleTempItem={handleToggleTempClient}
            onApply={handleApplyClients}
            getOptionId={getClientId}
            getOptionName={getClientName}
            emptyText="No clients found"
            containerSx={{ pb: 10 }}
          />
        </DialogContent>

        <Divider sx={{ border: "1px solid #F0F0F4" }} />

        <DialogActions className="create-space-actions">
          {mode === "edit" ? (
            <Button
              className="create-space-delete-btn"
              variant="outlined"
              size="small"
              onClick={() => setDeleteDialogOpen(true)}
            >
              Delete Space
            </Button>
          ) : (
            <Button
              onClick={handleCloseAndReset}
              className="create-space-cancel-btn"
            >
              Cancel
            </Button>
          )}

          <Button
            className="create-space-submit-btn"
            variant="contained"
            onClick={handleSave}
            disabled={!isFormValid || createSpaceMutation.isPending || updateSpaceMutation.isPending}
          >
            {createSpaceMutation.isPending || updateSpaceMutation.isPending
              ? "Saving..."
              : mode === "edit"
              ? "Save"
              : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      <CustomConfirmation
        isOpen={deleteDialogOpen}
        title="Do you want to delete this space?"
        actionBtnLabel="Delete"
        isLoading={deleteSpaceMutation.isPending}
        handleClose={() => setDeleteDialogOpen(false)}
        handleSuccess={handleDelete}
        width="auto"
      />
    </>
  );
};

export default CreateSpace;